import { useKBar, VisualState } from 'kbar';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { useFeature } from '@/features/feature-flags';
import { getSettingsMenuContext } from '@/features/settings/settings-menu-context';
import { openSettings } from '@/features/settings/settings-navigation';
import {
  settingsSearchEntries,
  type SettingsSearchEntry,
} from '@/features/settings/settings-search.config';
import { subscriptionStore } from '@/features/subscription';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { settingsStore } from '@/stores/settings.store';
import { userStore } from '@/stores/user.store';

import { createAction, Section } from './actions';
import {
  buildSettingsActions,
  resolveSettingsProviderEntries,
} from './settings-search-actions';

/**
 * Provider-backed settings-search entries, re-resolved each time the palette
 * opens so the rows track current data. Each open starts from an empty list
 * so a provider that fails or returns fewer rows cannot leave the previous
 * open's rows visible. A resolution is only accepted — and only published to
 * the deep-link registry — while its run is still current: a newer open or
 * closing the palette invalidates the run, dropping late resolutions
 *
 * Exported for tests; use `useSettingsAction` in app code
 */
export const useSettingsProviderEntries = (): SettingsSearchEntry[] => {
  const [providerEntries, setProviderEntries] = useState<SettingsSearchEntry[]>(
    []
  );
  const resolveRunRef = useRef(0);
  const { paletteVisible } = useKBar((state) => ({
    paletteVisible: state.visualState !== VisualState.hidden,
  }));

  useEffect(() => {
    if (!paletteVisible) return undefined;
    const run = ++resolveRunRef.current;
    setProviderEntries([]);
    const resolvedEntries: SettingsSearchEntry[] = [];
    resolveSettingsProviderEntries(
      getSettingsMenuContext(),
      (_provider, entries) => {
        if (run !== resolveRunRef.current) return;
        resolvedEntries.push(...entries);
        setProviderEntries([...resolvedEntries]);
      }
    );
    return () => {
      // invalidate the run when the palette closes (or the bar unmounts)
      resolveRunRef.current += 1;
    };
  }, [paletteVisible]);

  return providerEntries;
};

export const useSettingsAction = () => {
  const hasFeature = useFeature();
  const isInternalUser = useIsInternalUser();
  const hasInternalAccess = userStore.hasInternalAccess;
  const hasMcpServerAccess = subscriptionStore.hasMcpServerAccess;
  const hummingbirdEnabled = settingsStore.hummingbirdEnabled;
  const backendChoice = userStore.backendChoice;
  const providerEntries = useSettingsProviderEntries();

  const actions = useMemo(
    () =>
      buildSettingsActions(
        [...settingsSearchEntries, ...providerEntries],
        getSettingsMenuContext({
          hasFeature,
          hasMcpServerAccess,
          isInternalUser,
          hasInternalAccess,
          hummingbirdEnabled,
          backendChoice,
        }),
        openSettings
      ),
    [
      hasFeature,
      hasMcpServerAccess,
      isInternalUser,
      hasInternalAccess,
      hummingbirdEnabled,
      backendChoice,
      providerEntries,
    ]
  );

  const navigationToSettings = useMemo(
    () =>
      createAction({
        id: 'settings-root',
        name: 'Settings',
        section: Section.Navigation,
        icon: <Icons.settings size={14} />,
        children: () => actions,
      }),
    [actions]
  );

  return navigationToSettings;
};
